import React, { useEffect, useMemo, useState } from 'react';
import { Layers3, Loader2 } from 'lucide-react';
import { handleApiError } from '@/shared/api/requests';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useProjectAreaTags, useProjectUsers } from '../hooks';
import type { CreatePunchWorkflowBatchData, PunchItemWorkflow } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  workflow: PunchItemWorkflow | null;
  onSubmit: (data: CreatePunchWorkflowBatchData) => Promise<unknown>;
}

export const PunchWorkflowBatchDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  projectId,
  workflow,
  onSubmit,
}) => {
  const { data: locations = [] } = useProjectAreaTags(projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const [name, setName] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [manager, setManager] = useState('none');
  const [approver, setApprover] = useState('none');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => {
      const aLabel = a.layout_name ? `${a.layout_name} - ${a.name}` : a.name;
      const bLabel = b.layout_name ? `${b.layout_name} - ${b.name}` : b.name;
      return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
    }),
    [locations],
  );

  useEffect(() => {
    if (!open) return;
    setName(workflow ? `${workflow.name} — multiple locations` : '');
    setSelectedLocations([]);
    setManager('none');
    setApprover('none');
    setIsPrivate(false);
    setError(null);
  }, [open, workflow]);

  const toggleLocation = (locationId: number, selected: boolean) => {
    setSelectedLocations((current) => selected
      ? [...current, locationId]
      : current.filter((id) => id !== locationId));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workflow) return;
    if (selectedLocations.length < 2) {
      setError('Choose at least two locations to coordinate together.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        workflow: workflow.id,
        name: name.trim() || undefined,
        locations: selectedLocations,
        punch_manager: manager === 'none' ? null : Number(manager),
        final_approver: approver === 'none' ? null : Number(approver),
        is_private: isPrivate,
      });
      onOpenChange(false);
    } catch (submissionError) {
      setError(handleApiError(submissionError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Start a workflow at several locations</DialogTitle>
          <DialogDescription>
            Keep contractor visits together: every location finishes the current work group before the next crew is called.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="flex items-start gap-3 rounded-[1.375rem] border border-border bg-muted/50 p-4">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
              <Layers3 className="size-5" />
            </span>
            <div>
              <p className="font-display font-bold">{workflow?.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {workflow?.stage_count ?? 0} work groups · {workflow?.steps.length ?? 0} tasks per location
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="workflow-batch-name">Name this workflow</Label>
            <Input
              id="workflow-batch-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Kitchen devices — Units 101-103"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Locations</Label>
              <span className="text-xs font-semibold text-muted-foreground">
                {selectedLocations.length} selected
              </span>
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-border bg-card p-2">
              {sortedLocations.length === 0 && (
                <p className="p-3 text-sm text-muted-foreground">No project locations are available.</p>
              )}
              {sortedLocations.map((location) => {
                const checked = selectedLocations.includes(location.id);
                return (
                  <label
                    key={location.id}
                    className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors hover:bg-muted"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => toggleLocation(location.id, value === true)}
                    />
                    {location.layout_name ? `${location.layout_name} - ${location.name}` : location.name}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Person coordinating the work</Label>
              <Select value={manager} onValueChange={setManager}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {`${user.first_name} ${user.last_name}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Who gives final sign-off?</Label>
              <Select value={approver} onValueChange={setApprover}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {`${user.first_name} ${user.last_name}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <label className="flex items-center gap-3 rounded-xl border border-border bg-muted px-4 py-3 text-sm font-medium">
            <Checkbox checked={isPrivate} onCheckedChange={(checked) => setIsPrivate(checked === true)} />
            Limit these tasks to selected project members
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!workflow || submitting || selectedLocations.length < 2}>
              {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
              Start Workflow in {selectedLocations.length || ''} locations
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
